import { useEffect, useRef } from "react";
import {
  saveFlavorFormulaVersion,
  type SaveVersionItem,
  type SaveVersionResponse,
} from "@/services/formula_api";
import monitoring from "@/lib/monitoring";
import { normalizeAmountUnit } from "@/utils/formulation/normalizeAmountUnit";
import type { Variant } from "@/utils/types/variant";

// ── Tuning ────────────────────────────────────────────────────────────────
// Trailing-edge debounce window. Kept short so the autosave fires "promptly
// after each edit" rather than waiting for the user to stop editing — the
// intent is to persist the edit, not to wait for a long quiet period.
// 300ms — shared by normal autosave AND the Review-stage recompute. Lower than
// the old 800ms so the Review recompute feels live as the user fine-tunes;
// content-hash dedup (Rule 2 in fire()) keeps no-op edits from POSTing.
const DEBOUNCE_MS = 300;
// Hard ceiling: even if the user keeps editing continuously, force a save
// after this long. Stops "I've been dragging the slider for 90 seconds with
// no checkpoint" scenarios.
const MAX_INTERVAL_MS = 60_000;

// Parse "compound:<id>" / "food:<id>" back into the wire shape the server
// expects. Returns null when the id doesn't look like a server-emitted one
// (i.e. it's a mock fixture id) — those rows are skipped on save until the
// surrounding feature work makes mock-vs-server explicit.
function parseCompoundId(id: string): {
  substanceKind: "compound" | "food";
  substanceId: number;
} | null {
  const m = /^(compound|food):(\d+)$/.exec(id);
  if (!m) return null;
  return {
    substanceKind: m[1] as "compound" | "food",
    substanceId: Number(m[2]),
  };
}

function variantToItems(variant: Variant): SaveVersionItem[] {
  const out: SaveVersionItem[] = [];

  // FLAVOR rows — every entry needs a non-null amount per the server's
  // chk_flavor_amount_required CHECK. Use the entry's stored amountUnit so
  // non-ppm rows (e.g. imported as mg / g) round-trip correctly. Old in-memory
  // entries that pre-date the amountUnit field fall back to "ppm".
  //
  // Normalize before serializing so the DB stores the value in its
  // best-fit unit (e.g. "0.5 ppm" written as "500 ppb"). dtoToVariant also
  // normalizes on read, so this is belt-and-braces and any drift between
  // the two implementations would show up immediately as autosave churn.
  for (const f of variant.formulation) {
    const parsed = parseCompoundId(f.compoundId);
    if (!parsed) continue;            // skip mock fixture ids on save
    const norm = normalizeAmountUnit(f.ppm, f.amountUnit ?? "ppm");
    out.push({
      category: "FLAVOR",
      substanceKind: parsed.substanceKind,
      substanceId: parsed.substanceId,
      amountValue: norm.value,
      amountUnit: norm.unit,
    });
  }

  // FIXED_INGREDIENT rows — ppm of 0 is the "not decided yet" placeholder
  // that dtoToVariant uses on the read side, so we round-trip it back to
  // null on save. Any non-zero ppm is treated as a real user-set amount,
  // saved in whatever unit the entry was loaded with (default ppm).
  for (const f of variant.fixedIngredients ?? []) {
    const parsed = parseCompoundId(f.compoundId);
    if (!parsed) continue;
    const isPlaceholder = f.ppm === 0;
    const norm = isPlaceholder
      ? null
      : normalizeAmountUnit(f.ppm, f.amountUnit ?? "ppm");
    out.push({
      category: "FIXED_INGREDIENT",
      substanceKind: parsed.substanceKind,
      substanceId: parsed.substanceId,
      amountValue: norm ? norm.value : null,
      amountUnit: norm ? norm.unit : null,
    });
  }

  return out;
}

type UseAutosaveFormulaArgs = {
  flavorId: string | null;
  activeVariant: Variant | null;
  /**
   * Called after a successful version save. The caller patches the active
   * variant's currentVersionId so downstream consumers (Add Ingredients
   * search, viz hook) re-query against the new version. Without this,
   * `useFormulationVariants` keeps the variant pinned to the value it saw
   * on first mount — its `seededFromServerRef` guard intentionally ignores
   * subsequent refetches of `useFlavorFormulas`.
   */
  onVersionSaved?: (variantId: string, response: SaveVersionResponse) => void;
  /**
   * When true, the formulation is being fine-tuned in the AF Review stage.
   * The debounce still arms (despite autosave being paused for AF), but fire()
   * routes to onReviewRecompute instead of saveVersion — no version is written.
   */
  reviewMode?: boolean;
  /**
   * Called (debounced + once immediately on Review entry) with the current
   * items during Review. Fire-and-forget; receives an AbortSignal that is
   * aborted when a newer edit supersedes this recompute.
   */
  onReviewRecompute?: (items: SaveVersionItem[], signal: AbortSignal) => void;
};

export type UseAutosaveFormulaResult = {
  /**
   * Mark a variant as already-synced-with-server. Call this after a
   * server-confirmed state update (import-commit, restore) that triggers an
   * in-memory variant patch — without this, the patch would schedule a
   * redundant autosave POST that server-side dedup would discard at the cost
   * of an extra round-trip plus a formulas-list refetch (~2s total).
   */
  markSynced: (variant: Variant) => void;
  /**
   * Pause autosave. All in-flight + scheduled saves are dropped; future
   * variant changes won't schedule a save until `resume()` is called.
   * Used by Auto-Formulate to keep the Running + Review states out of
   * version history — only the final Accept fires one save.
   */
  pause: () => void;
  /**
   * Resume autosave. If the active variant has drifted from the last-saved
   * baseline since `pause()`, re-arms the standard debounce. If state is
   * unchanged (e.g., Reject restored to snapshot which == baseline), no
   * save is scheduled.
   */
  resume: () => void;
  /**
   * Save immediately, skipping debounce/ceiling timers and any pause flag.
   * Resolves when the save round-trip completes (success or caught error).
   * onVersionSaved still fires synchronously inside the save handler — the
   * caller doesn't need to listen here for the new versionId. Used by
   * Auto-Formulate.accept() to commit the proposed flask in one shot.
   */
  flush: () => Promise<void>;
};

export function useAutosaveFormula({
  flavorId,
  activeVariant,
  onVersionSaved,
  reviewMode = false,
  onReviewRecompute,
}: UseAutosaveFormulaArgs): UseAutosaveFormulaResult {
  // Mirror in a ref so the long-lived fire() / visibility callbacks read the
  // latest callback without resubscribing.
  const onVersionSavedRef = useRef<typeof onVersionSaved>(onVersionSaved);
  onVersionSavedRef.current = onVersionSaved;

  const reviewModeRef = useRef(reviewMode);
  reviewModeRef.current = reviewMode;
  const onReviewRecomputeRef = useRef<typeof onReviewRecompute>(onReviewRecompute);
  onReviewRecomputeRef.current = onReviewRecompute;
  // Separate in-flight controller + dedup baseline for the review path so it
  // never touches the save path's inFlightRef / lastSavedRef.
  const reviewInFlightRef = useRef<AbortController | null>(null);
  const lastReviewSerializedRef = useRef<string | null>(null);

  // Latest variant, captured outside React state so the timer can read the
  // current value on fire.
  const latestRef = useRef<Variant | null>(activeVariant);
  latestRef.current = activeVariant;

  // flavorId mirror so the timer/visibility callbacks (declared once) read
  // the current value rather than a stale closure.
  const flavorIdRef = useRef<string | null>(flavorId);
  flavorIdRef.current = flavorId;

  // Deferred timer + ceiling timer + in-flight controller.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ceilingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<AbortController | null>(null);
  // Pause flag set by Auto-Formulate (run → pause; accept/reject → resume).
  // When paused, fire() short-circuits and the scheduling effect doesn't arm.
  const pausedRef = useRef(false);
  // Track the last variant we saved so we don't re-fire on tab switches that
  // didn't actually edit anything.
  const lastSavedRef = useRef<{ formulaId: number; serialized: string } | null>(
    null,
  );

  const cancelTimers = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    if (ceilingRef.current) clearTimeout(ceilingRef.current);
    ceilingRef.current = null;
  };

  // `forceSave` is set by flush() (AF Accept): even though reviewModeRef is
  // still true at the synchronous moment accept() calls flush (the render that
  // flips phase→'saving' hasn't committed yet), Accept MUST persist a version,
  // so it forces the save path rather than the review-recompute path.
  const fire = async (opts?: { forceSave?: boolean }) => {
    cancelTimers();
    const forceSave = opts?.forceSave === true;
    // In review mode the AF flow has paused autosave; we still recompute, but
    // never save. Outside review, the pause short-circuits as before.
    if (pausedRef.current && !reviewModeRef.current && !forceSave) return;

    const variant = latestRef.current;
    const fid = flavorIdRef.current;
    if (!variant || !variant.formulaId || fid === null) return;

    const items = variantToItems(variant);

    // ── Review-stage recompute path — fire-and-forget, no version write ──
    if (reviewModeRef.current && !forceSave) {
      const serializedReview = JSON.stringify(items);
      if (lastReviewSerializedRef.current === serializedReview) return; // dedup
      lastReviewSerializedRef.current = serializedReview;
      reviewInFlightRef.current?.abort();
      const ctrl = new AbortController();
      reviewInFlightRef.current = ctrl;
      onReviewRecomputeRef.current?.(items, ctrl.signal);
      return;
    }

    const serialized = JSON.stringify(items);
    const last = lastSavedRef.current;

    // Rule 1: skip empty-flask autosave ONLY when the previous saved state
    // was also empty (or unset). A brand-new flask landing empty shouldn't
    // generate a redundant version row. But if the user deliberately clears
    // a previously-non-empty formulation, persist that empty state — the
    // viz/loss/radar/etc. consumers need to update to reflect "nothing in
    // the flask".
    if (items.length === 0) {
      const lastSerialized = last?.serialized;
      if (lastSerialized === undefined || lastSerialized === "[]") return;
    }

    // Rule 2: skip when the serialized items are byte-identical to the last
    // saved baseline. lastSavedRef is seeded by the formulaId-first-seen
    // effect below so a freshly server-loaded variant is already "saved" —
    // no POST fires unless the user has actually edited the rows.
    if (
      last &&
      last.formulaId === variant.formulaId &&
      last.serialized === serialized
    ) {
      return;
    }

    // Cancel any save still in flight — fresh edit wins.
    inFlightRef.current?.abort();
    const ctrl = new AbortController();
    inFlightRef.current = ctrl;

    // Optimistically stamp the saved-baseline BEFORE awaiting. A near-
    // simultaneous fire() with identical content — e.g. AF Accept's flush()
    // racing the formulation-change debounce it also triggers — then dedups
    // (Rule 2 above) instead of issuing a second POST that would collide on
    // version_number server-side. Rolled back on failure so the edit re-fires.
    const prevSaved = lastSavedRef.current;
    lastSavedRef.current = { formulaId: variant.formulaId, serialized };

    try {
      const response = await saveFlavorFormulaVersion(
        fid,
        variant.formulaId,
        { saveType: "autosave", items },
        ctrl.signal,
      );
      // Baseline already stamped optimistically above.
      // Forward the new versionId so the caller can patch the active variant.
      // useFormulationVariants' first-load guard means a vanilla
      // invalidateQueries WON'T propagate currentVersionId — Add Ingredients
      // would keep using the stale versionId and never see the new viz row's
      // ingredient_sort.
      onVersionSavedRef.current?.(variant.id, response);
      // We intentionally do NOT invalidate flavorFormulaKeys here. The only
      // active consumer that needs fresh `formulas[].items` is
      // WorkbenchPage's `dropDeltaByCompoundId` map, and that map is only
      // read once `replacements.status === 'ready'`. The replacement worker
      // completion's SSE handler in useWorkbenchReplacements does the
      // formulas invalidate at the moment fresh data is actually needed —
      // saving a network round-trip per save in the common case where the
      // user is editing faster than the worker can complete.
    } catch (err) {
      // Roll back the optimistic baseline so this edit re-fires — unless a
      // newer save has already stamped its own baseline (leave that intact).
      if (
        lastSavedRef.current?.formulaId === variant.formulaId &&
        lastSavedRef.current?.serialized === serialized
      ) {
        lastSavedRef.current = prevSaved;
      }
      if ((err as { name?: string } | null)?.name === "AbortError") return;
      // Don't surface autosave failures in the UI — the user is mid-edit.
      // Console-level for now; we can wire telemetry later.
      monitoring.warn("workbench_autosave_failed", err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (inFlightRef.current === ctrl) inFlightRef.current = null;
    }
  };

  // Stringify both formulation arrays so React only re-runs the scheduling
  // effect when either section actually changed, not on every parent
  // re-render. Deriving these above the effect keeps the dep array as clean
  // string-or-null types (vs. the `false | string` mix you'd get from inline
  // `activeVariant && JSON.stringify(...)`).
  const formulationKey = activeVariant
    ? JSON.stringify(activeVariant.formulation)
    : null;
  const fixedKey = activeVariant
    ? JSON.stringify(activeVariant.fixedIngredients ?? [])
    : null;

  // Track which formulaIds we've already baselined so the seed effect only
  // stamps the FIRST time a given formula's data lands. Re-running on every
  // edit would re-stamp the user's unsaved changes as "saved" and break
  // autosave. The set lives for the WorkbenchPage's lifetime (route change
  // re-mounts the page and clears it), matching useFormulationVariants's
  // `seededFromServerRef` lifecycle.
  const seededFormulaIdsRef = useRef<Set<number>>(new Set());

  // Seed effect — runs whenever activeVariant changes. The first time we see
  // a given formulaId, capture its server-loaded shape as the autosave
  // baseline so the debounced fire() recognizes it as already-saved.
  // Without this, the initial mount → seed → effect-schedules-debounce →
  // fire() chain would always send one redundant autosave per page visit.
  useEffect(() => {
    const v = latestRef.current;
    const fid = v?.formulaId;
    if (!v || fid == null) return;
    if (seededFormulaIdsRef.current.has(fid)) return;
    seededFormulaIdsRef.current.add(fid);
    lastSavedRef.current = {
      formulaId: fid,
      serialized: JSON.stringify(variantToItems(v)),
    };
  }, [activeVariant?.formulaId, formulationKey, fixedKey]);

  useEffect(() => {
    if (!activeVariant?.formulaId || flavorId === null) return;
    // Pause blocks normal autosave, but review mode still arms the recompute.
    if (pausedRef.current && !reviewModeRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void fire(), DEBOUNCE_MS);
    // No ceiling "force save" in review mode — there's no version to force.
    if (!ceilingRef.current && !reviewModeRef.current) {
      ceilingRef.current = setTimeout(() => void fire(), MAX_INTERVAL_MS);
    }
    return () => {
      // pending save/recompute is allowed to run across re-renders.
    };
  }, [flavorId, activeVariant?.formulaId, formulationKey, fixedKey, reviewMode]);

  // On Review entry, recompute immediately (so Add Ingredients matches the
  // proposal without waiting for an edit). Reset the dedup baseline so the
  // entry recompute always fires. On exit, abort any in-flight recompute.
  useEffect(() => {
    if (reviewMode) {
      lastReviewSerializedRef.current = null;
      void fire();
    } else {
      reviewInFlightRef.current?.abort();
      reviewInFlightRef.current = null;
      lastReviewSerializedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewMode]);

  // Final flush on unmount + visibilitychange. Uses regular fetch with an
  // AbortController-driven save; pages that close mid-fetch may race the
  // network teardown — sendBeacon would close that gap and is a follow-up
  // when page-close durability becomes a concrete requirement.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") void fire();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      inFlightRef.current?.abort();
      cancelTimers();
      void fire();
    };
    // fire is stable enough — only flavorId / activeVariant changes matter,
    // and those drive the other effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Public surface — called by the page after a server-confirmed update so
  // the in-memory patch doesn't schedule a wasted dedup-only autosave.
  const markSynced = (variant: Variant) => {
    if (!variant.formulaId) return;
    const items = variantToItems(variant);
    lastSavedRef.current = {
      formulaId: variant.formulaId,
      serialized: JSON.stringify(items),
    };
    // Drop any debounce/ceiling timer that was already scheduled for this
    // edit so the post-debounce fire() doesn't even compute the hash.
    cancelTimers();
  };

  const pause = () => {
    pausedRef.current = true;
    cancelTimers();
    // Abort any save currently in flight — its result would invalidate the
    // pause invariant (a version row would be created mid-AF).
    inFlightRef.current?.abort();
    inFlightRef.current = null;
  };

  const resume = () => {
    pausedRef.current = false;
    // Re-arm if state has drifted from the last-saved baseline.
    const v = latestRef.current;
    if (!v || !v.formulaId) return;
    const items = variantToItems(v);
    const serialized = JSON.stringify(items);
    const last = lastSavedRef.current;
    if (!last || last.formulaId !== v.formulaId || last.serialized !== serialized) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => void fire(), DEBOUNCE_MS);
    }
  };

  // Save NOW. Bypasses the debounce/ceiling timers and clears pausedRef so
  // an AF-suspended autosave can commit on Accept. Always awaits the same
  // fire() the timers would call — onVersionSaved fires inside fire() on
  // success, so callers can rely on it landing during the await rather
  // than registering a second listener.
  const flush = async (): Promise<void> => {
    pausedRef.current = false;
    cancelTimers();
    await fire({ forceSave: true });
  };

  return { markSynced, pause, resume, flush };
}
